import type { User } from "firebase/auth";
import { type FormEvent, type Ref, useEffect, useRef, useState } from "react";
import { galText } from "./accounts";
import * as api from "./api";
import { DEPOT_COLUMNS, TRUCK_COLUMNS } from "./columns";
import {
  type DepotDraft,
  type DepotField,
  depotChange,
  depotDraft,
  depotSavedText,
  homeAreasText,
  hoursText,
  pointText,
  type TruckDraft,
  type TruckField,
  truckChange,
  truckDraft,
  truckSavedText,
} from "./fleet";
import { LocationCard } from "./LocationCard";
import { type Saved, saveFailure } from "./save";
import { sortRows } from "./sort";
import { SortSelect, SortTh, useSort } from "./SortHeader";
import { Skeleton } from "./Skeleton";

type Editing = { kind: "depot" | "truck"; id: string | null };

function FieldError({ id, text }: { id: string; text: string | undefined }) {
  return text ? (
    <p id={id} role="alert" className="error">
      {text}
    </p>
  ) : null;
}

function DepotForm({
  found,
  depot,
  saving,
  onSave,
  onCancel,
}: {
  found: api.Fleet;
  depot: api.Depot | null;
  saving: boolean;
  onSave: (change: api.DepotFields) => Promise<boolean>;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<DepotDraft>(() => depotDraft(depot, found.defaults));
  const [problem, setProblem] = useState<{ field: DepotField; text: string } | null>(null);
  const first = useRef<HTMLInputElement>(null);
  useEffect(() => first.current?.focus(), []);
  const set = (field: DepotField, value: string) => {
    setDraft({ ...draft, [field]: value });
    setProblem(null);
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const change = depotChange(draft);
    if ("text" in change) {
      setProblem(change);
      document.getElementById(`depot-${change.field}`)?.focus();
      return;
    }
    await onSave(change);
  };
  const input = (field: DepotField, label: string, type = "text", ref?: Ref<HTMLInputElement>) => (
    <label key={field}>
      {label}
      <input
        id={`depot-${field}`}
        data-testid={`depot-${field}`}
        ref={ref}
        type={type}
        value={draft[field]}
        aria-invalid={problem?.field === field || undefined}
        aria-describedby={problem?.field === field ? "depot-error" : undefined}
        onChange={(e) => set(field, e.target.value)}
      />
    </label>
  );
  return (
    <form className="fleet-form" data-testid="depot-form" onSubmit={(e) => void submit(e)} noValidate>
      <h4>{depot ? `Change ${depot.name}` : "Add a depot"}</h4>
      <div className="fields">
        {input("name", "Name", "text", first)}
        {input("address", "Address")}
        {input("opens", "Opens", "time")}
        {input("closes", "Closes", "time")}
      </div>
      <FieldError id="depot-error" text={problem?.text} />
      <p className="hint">Each truck of the depot starts and ends its route at the depot point. When you save, the point comes from the address. Demo data is not sent to Google, so its point does not change. You can move the point on the map.</p>
      <div className="actions">
        <button className="primary" data-testid="depot-save" disabled={saving}>
          {saving ? "Saving…" : depot ? "Save the depot" : "Add the depot"}
        </button>
        <button type="button" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function TruckForm({
  found,
  truck,
  saving,
  onSave,
  onRemove,
  onCancel,
}: {
  found: api.Fleet;
  truck: api.Truck | null;
  saving: boolean;
  onSave: (change: api.TruckFields, homeAreas: string[] | null) => Promise<boolean>;
  onRemove: () => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<TruckDraft>(() => truckDraft(truck, found));
  const saved = truck?.home_areas.map((area) => area.id) ?? [];
  const [homes, setHomes] = useState<string[]>(saved);
  const homesChanged = !(homes.length === saved.length && saved.every((id) => homes.includes(id)));
  const [problem, setProblem] = useState<{ field: TruckField; text: string } | null>(null);
  const [asking, setAsking] = useState(false);
  const first = useRef<HTMLInputElement>(null);
  const removeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => first.current?.focus(), []);
  const set = (next: Partial<TruckDraft>) => {
    setDraft({ ...draft, ...next });
    setProblem(null);
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const taken = found.trucks.filter((other) => other.id !== truck?.id).map((other) => other.code);
    const change = truckChange(draft, taken);
    if ("text" in change) {
      setProblem(change);
      document.getElementById(`truck-${change.field}`)?.focus();
      return;
    }
    await onSave(change, homesChanged ? homes : null);
  };
  const wrong = (field: TruckField) => ({
    "aria-invalid": problem?.field === field || undefined,
    "aria-describedby": problem?.field === field ? "truck-error" : undefined,
  });
  return (
    <form className="fleet-form" data-testid="truck-form" onSubmit={(e) => void submit(e)} noValidate>
      <h4>{truck ? `Change truck ${truck.code}` : "Add a truck"}</h4>
      <div className="fields">
        <label>
          Truck
          <input
            id="truck-code"
            data-testid="truck-code"
            ref={first}
            value={draft.code}
            autoCapitalize="characters"
            readOnly={Boolean(truck && truck.routes > 0)}
            {...wrong("code")}
            onChange={(e) => set({ code: e.target.value })}
          />
        </label>
        <label>
          Depot
          <select id="truck-hub_id" data-testid="truck-depot" value={draft.hub_id} {...wrong("hub_id")} onChange={(e) => set({ hub_id: e.target.value })}>
            <option value="">Choose…</option>
            {found.depots.map((depot) => (
              <option key={depot.id} value={depot.id}>
                {depot.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Capacity (gal)
          <input
            id="truck-capacity"
            data-testid="truck-capacity"
            inputMode="numeric"
            value={draft.capacity}
            {...wrong("capacity")}
            onChange={(e) => set({ capacity: e.target.value })}
          />
        </label>
        <label>
          Shift starts
          <input id="truck-shift_start" data-testid="truck-shift-start" type="time" value={draft.shift_start} {...wrong("shift_start")} onChange={(e) => set({ shift_start: e.target.value })} />
        </label>
        <label>
          Shift ends
          <input id="truck-shift_end" data-testid="truck-shift-end" type="time" value={draft.shift_end} {...wrong("shift_end")} onChange={(e) => set({ shift_end: e.target.value })} />
        </label>
        {truck && (
          <label className="check">
            <input type="checkbox" data-testid="truck-active" checked={draft.active} onChange={(e) => set({ active: e.target.checked })} /> Active
          </label>
        )}
      </div>
      {found.areas.length > 0 && (
        <fieldset className="home-areas" data-testid="home-areas-form">
          <legend>Home areas{truck ? ` of truck ${truck.code}` : ""}</legend>
          {found.areas.map((area) => (
            <label key={area.id} className="check">
              <input
                type="checkbox"
                data-testid="home-area"
                data-code={area.code}
                checked={homes.includes(area.id)}
                onChange={(e) => setHomes(e.target.checked ? [...homes, area.id] : homes.filter((id) => id !== area.id))}
              />{" "}
              {area.code}
            </label>
          ))}
        </fieldset>
      )}
      {found.areas.length > 0 && <p className="hint">The truck works in its home areas. Other trucks can have the same areas.</p>}
      <FieldError id="truck-error" text={problem?.text} />
      {truck && truck.routes > 0 && (
        <p className="hint">
          Routes use truck {truck.code}, so its code stays and you cannot remove it. To stop its use, clear Active.
        </p>
      )}
      <div className="actions">
        <button className="primary" data-testid="truck-save" disabled={saving}>
          {saving ? "Saving…" : truck ? "Save the truck" : "Add the truck"}
        </button>
        <button type="button" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        {truck && truck.routes === 0 && (
          <button type="button" ref={removeRef} className="quiet" data-testid="truck-remove" onClick={() => setAsking(true)} disabled={saving || asking}>
            Remove the truck
          </button>
        )}
      </div>
      {truck && asking && (
        <p className="confirm" data-testid="truck-remove-ask">
          Remove truck {truck.code}? You cannot undo this.{" "}
          <button type="button" className="danger" data-testid="truck-remove-confirm" disabled={saving} onClick={onRemove}>
            Remove the truck
          </button>{" "}
          <button
            type="button"
            data-testid="truck-remove-keep"
            autoFocus
            onClick={() => {
              setAsking(false);
              requestAnimationFrame(() => removeRef.current?.focus());
            }}
          >
            Keep it
          </button>
        </p>
      )}
    </form>
  );
}

/** Roadmap 062: the depots and the trucks that the route planner uses. A depot is a hub. */
export function FleetSettings({ user, timeZone }: { user: User; timeZone: string }) {
  const [found, setFound] = useState<api.Fleet | "failed" | undefined>(undefined);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<Saved | null>(null);
  const [depotSort, setDepotSort] = useSort("depots", DEPOT_COLUMNS);
  const [truckSort, setTruckSort] = useSort("trucks", TRUCK_COLUMNS);
  const status = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    user
      .getIdToken()
      .then(api.fleet)
      .then(setFound, () => setFound("failed"));
  }, [user]);

  if (found === "failed") {
    return (
      <fieldset className="fleet">
        <legend className="visually-hidden">Fleet</legend>
        <p className="error">Cannot load the fleet. Try again later.</p>
      </fieldset>
    );
  }
  if (found === undefined) {
    return (
      <fieldset className="fleet" aria-busy="true">
        <legend className="visually-hidden">Fleet</legend>
        <Skeleton shape="form" label="Loading the fleet" />
      </fieldset>
    );
  }
  const run = async (thing: string, action: (token: string) => Promise<api.Fleet>, done: string | ((next: api.Fleet) => string)) => {
    setSaving(true);
    setMessage(null);
    try {
      const next = await action(await user.getIdToken());
      setFound(next);
      setEditing(null);
      setMessage({ ok: true, text: typeof done === "string" ? done : done(next) });
      requestAnimationFrame(() => status.current?.focus());
      return true;
    } catch (e) {
      setMessage(saveFailure(e, thing));
      return false;
    } finally {
      setSaving(false);
    }
  };
  const open = (next: Editing) => {
    setEditing(next);
    setMessage(null);
  };
  const isEditing = (kind: Editing["kind"], id: string | null) => editing?.kind === kind && editing.id === id;
  const depotForm = (depot: api.Depot | null) => (
    <DepotForm
      found={found}
      depot={depot}
      saving={saving}
      onCancel={() => setEditing(null)}
      onSave={(change) => {
        const id = depot?.id ?? crypto.randomUUID();
        const action = depot ? (token: string) => api.changeDepot(token, id, change) : (token: string) => api.addDepot(token, { id, ...change });
        return run("The depot", action, (next) =>
          depotSavedText(change.name, Boolean(depot), next.depots.find((found) => found.id === id)?.point ?? null, Boolean(next.address_not_sent)),
        );
      }}
    />
  );
  const truckForm = (truck: api.Truck | null) => (
    <TruckForm
      found={found}
      truck={truck}
      saving={saving}
      onCancel={() => setEditing(null)}
      onRemove={() => truck && void run("The truck", (token) => api.removeTruck(token, truck.id), `Removed: truck ${truck.code}.`)}
      onSave={(change, homeAreas) => {
        const id = truck?.id ?? crypto.randomUUID();
        const action = async (token: string) => {
          const next = truck ? await api.changeTruck(token, id, change) : await api.addTruck(token, { id, ...change });
          return homeAreas ? api.setHomeAreas(token, id, homeAreas) : next;
        };
        return run(homeAreas ? "The truck and its home areas" : "The truck", action, truckSavedText(change.code, Boolean(truck), homeAreas !== null));
      }}
    />
  );
  const span = (columns: number) => columns + (found.can_edit ? 1 : 0);
  const changeCell = (label: string, next: Editing) =>
    found.can_edit && (
      <td data-label="" className="row-actions">
        <button type="button" data-testid="fleet-change" aria-label={`Change ${label}`} disabled={saving} onClick={() => open(next)}>
          Change
        </button>
      </td>
    );
  return (
    <fieldset className="fleet" data-testid="fleet-settings">
      <legend className="visually-hidden">Fleet</legend>
      <p className="hint">Each truck starts and ends its route at its depot. The route planner uses the capacity and the shift of each active truck.</p>
      <h3>Depots</h3>
      {found.depots.length === 0 ? (
        <p data-testid="depot-none">No depots. Add a depot before you add a truck.</p>
      ) : (
        <>
          <SortSelect id="depots-select" columns={DEPOT_COLUMNS} sort={depotSort} onSort={setDepotSort} fallback="Depot" />
          <div className="table-scroll" tabIndex={0} role="region" aria-label="Depots">
            <table className="list" data-testid="depot-table" data-cols="depots-select">
              <thead>
                <tr>
                  {DEPOT_COLUMNS.map((column) => (
                    <SortTh key={column.key} column={column} sort={depotSort} onSort={setDepotSort} />
                  ))}
                  {found.can_edit && (
                    <th scope="col">
                      <span className="visually-hidden">Change</span>
                    </th>
                  )}
                </tr>
              </thead>
              {sortRows(found.depots, DEPOT_COLUMNS, depotSort).map((depot) => (
                <tbody key={depot.id} className="item">
                  <tr data-testid="depot-row" data-name={depot.name}>
                    <th scope="row">{depot.name}</th>
                    <td data-label="Address">{depot.address || "–"}</td>
                    <td data-label="Point">{pointText(depot)}</td>
                    <td data-label="Opens">{depot.opens}</td>
                    <td data-label="Closes">{depot.closes}</td>
                    {changeCell(depot.name, { kind: "depot", id: depot.id })}
                  </tr>
                  {isEditing("depot", depot.id) && (
                    <tr className="detail-row">
                      <td colSpan={span(DEPOT_COLUMNS.length)} data-label="">
                        {depotForm(depot)}
                        <LocationCard
                          title="Depot point"
                          doorName="Depot point"
                          doorWord="depot"
                          door={depot.point}
                          hasTank={false}
                          canDoor={found.can_edit}
                          canTank={false}
                          address={depot.address}
                          timeZone={timeZone}
                          user={user}
                          save={async (change) => setFound(await api.setDepotLocation(await user.getIdToken(), depot.id, change))}
                        />
                      </td>
                    </tr>
                  )}
                </tbody>
              ))}
            </table>
          </div>
        </>
      )}
      {found.can_edit &&
        (isEditing("depot", null) ? (
          depotForm(null)
        ) : (
          <p className="actions">
            <button type="button" data-testid="depot-add" disabled={saving} onClick={() => open({ kind: "depot", id: null })}>
              Add a depot
            </button>
          </p>
        ))}
      <h3>Trucks</h3>
      {found.trucks.length === 0 ? (
        <p data-testid="truck-none">No trucks.</p>
      ) : (
        <>
          <SortSelect id="trucks-select" columns={TRUCK_COLUMNS} sort={truckSort} onSort={setTruckSort} fallback="Truck" />
          <div className="table-scroll" tabIndex={0} role="region" aria-label="Trucks">
            <table className="list" data-testid="truck-table" data-cols="trucks-select">
              <thead>
                <tr>
                  {TRUCK_COLUMNS.map((column) => (
                    <SortTh key={column.key} column={column} sort={truckSort} onSort={setTruckSort} />
                  ))}
                  {found.can_edit && (
                    <th scope="col">
                      <span className="visually-hidden">Change</span>
                    </th>
                  )}
                </tr>
              </thead>
              {sortRows(found.trucks, TRUCK_COLUMNS, truckSort).map((truck) => (
                <tbody key={truck.id} className="item">
                  <tr data-testid="truck-row" data-code={truck.code} className={truck.active ? undefined : "muted"}>
                    <th scope="row">{truck.code}</th>
                    <td data-label="Depot">{truck.hub_name}</td>
                    <td data-label="Capacity" className="num">
                      {galText(truck.capacity_gal)}
                    </td>
                    <td data-label="Shift">{hoursText(truck.shift_start, truck.shift_end)}</td>
                    <td data-label="Status">{truck.active ? "Active" : <span className="chip-flag">Inactive</span>}</td>
                    <td data-label="Home areas" data-testid="truck-home-areas">
                      {homeAreasText(truck)}
                    </td>
                    <td data-label="Routes" className="num">
                      {truck.routes}
                    </td>
                    {changeCell(`truck ${truck.code}`, { kind: "truck", id: truck.id })}
                  </tr>
                  {isEditing("truck", truck.id) && (
                    <tr className="detail-row">
                      <td colSpan={span(TRUCK_COLUMNS.length)} data-label="">
                        {truckForm(truck)}
                      </td>
                    </tr>
                  )}
                </tbody>
              ))}
            </table>
          </div>
        </>
      )}
      {found.can_edit &&
        found.depots.length > 0 &&
        (isEditing("truck", null) ? (
          truckForm(null)
        ) : (
          <p className="actions">
            <button type="button" data-testid="truck-add" disabled={saving} onClick={() => open({ kind: "truck", id: null })}>
              Add a truck
            </button>
          </p>
        ))}
      <p ref={status} tabIndex={-1} role="status" data-testid="fleet-status" className={message && !message.ok ? "error" : undefined}>
        {message?.text}
      </p>
    </fieldset>
  );
}
